import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react"
import {
  clearActionHistory,
  facesFromHistorySnapshot,
  historyJumpCameraPatches,
  historyStorageKey,
  loadActionHistory,
  migrateActionHistory,
  popNewestActionHistory,
  pushActionHistory,
  rewindActionHistory,
  snapshotFromVisual,
  startCameraAfterHistoryRestore,
  type HistoryEntry,
  type HistorySnapshot,
} from "@/lib/location-scene-3d/action-history"
import { cameraPlaceAimFromStart } from "@/lib/location-scene-3d/camera-place"
import type { CameraPath } from "@/lib/location-scene-3d/camera-path"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"
import type { PlatformLayerState } from "@/lib/location-scene-3d/platform-layer"
import {
  defaultSceneLight,
  emptySceneLayout,
  normalizeSceneLayout,
  type SceneCameraState,
  type SceneLayoutState,
  type SceneObjectState,
} from "@/lib/location-scene-3d/scene-state"

const BASELINE_LABEL_KEY = "locationsPage.scene3d.historyActions.baseline"

/** Seed empty history with a baseline entry (undo needs length >= 2). */
export function seedBaselineHistory(historyKey: string, snapshot: HistorySnapshot): HistoryEntry[] {
  return pushActionHistory(historyKey, {
    labelKey: BASELINE_LABEL_KEY,
    snapshot,
  })
}

/** Load existing history or seed a baseline from the current snapshot. */
export function ensureBaselineHistory(historyKey: string, captureSnapshot: () => HistorySnapshot): HistoryEntry[] {
  if (loadActionHistory(historyKey).length === 0) {
    return seedBaselineHistory(historyKey, captureSnapshot())
  }
  return loadActionHistory(historyKey)
}

type UseLocationSceneHistoryArgs = {
  projectId: number
  locationId: number | null
  boundScene3dId: number | null
  scene3dId: number | null
  engineRef: MutableRefObject<Scene3dEngine | null>
  platformVisualRef: MutableRefObject<PlatformLayerState | null>
  startCameraRef: MutableRefObject<SceneCameraState | null>
  setHasStartCamera: Dispatch<SetStateAction<boolean>>
  skipHistoryRef: MutableRefObject<boolean>
  dirtyRef: MutableRefObject<boolean>
  existsRef: MutableRefObject<boolean>
  setExists: Dispatch<SetStateAction<boolean>>
  setObjects: Dispatch<SetStateAction<SceneObjectState[]>>
  setCameraPath: Dispatch<SetStateAction<CameraPath>>
  setPlatformLayout: Dispatch<SetStateAction<SceneLayoutState | null>>
  setHistoryOpen: Dispatch<SetStateAction<boolean>>
  queuePersist: () => void
}

export function useLocationSceneHistory({
  projectId,
  locationId,
  boundScene3dId,
  scene3dId,
  engineRef,
  platformVisualRef,
  startCameraRef,
  setHasStartCamera,
  skipHistoryRef,
  dirtyRef,
  existsRef,
  setExists,
  setObjects,
  setCameraPath,
  setPlatformLayout,
  setHistoryOpen,
  queuePersist,
}: UseLocationSceneHistoryArgs) {
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([])
  const [redoStack, setRedoStack] = useState<HistoryEntry[]>([])

  const historyKey = useMemo(() => {
    const sceneKey = String(boundScene3dId ?? scene3dId ?? "draft")
    return historyStorageKey(projectId, locationId, sceneKey)
  }, [boundScene3dId, locationId, projectId, scene3dId])

  const previousHistoryKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const prevKey = previousHistoryKeyRef.current
    previousHistoryKeyRef.current = historyKey
    if (prevKey != null && prevKey !== historyKey) {
      setHistoryEntries(migrateActionHistory(prevKey, historyKey))
    } else {
      setHistoryEntries(loadActionHistory(historyKey))
    }
    setRedoStack([])
  }, [historyKey])

  const stampActiveTake = useCallback(
    (snapshot: HistorySnapshot): HistorySnapshot => {
      const set = engineRef.current?.getCameraPathTakes()
      if (!set) return snapshot
      const active = set.takes.find((take) => take.id === set.activeId)
      return {
        ...snapshot,
        active_camera_path_id: set.activeId,
        active_camera_path_name: active?.name ?? null,
      }
    },
    [engineRef],
  )

  const pushHistorySnapshot = useCallback(
    (labelKey: string, snapshot: HistorySnapshot, labelParams?: Record<string, string | number>) => {
      if (skipHistoryRef.current) return
      const next = pushActionHistory(historyKey, { labelKey, labelParams, snapshot: stampActiveTake(snapshot) })
      setHistoryEntries(next)
      setRedoStack([])
    },
    [historyKey, skipHistoryRef, stampActiveTake],
  )
  const pushHistorySnapshotRef = useRef(pushHistorySnapshot)
  pushHistorySnapshotRef.current = pushHistorySnapshot

  const captureCurrentSnapshot = useCallback((): HistorySnapshot => {
    const visual = platformVisualRef.current
    const state = engineRef.current?.getSceneState()
    return stampActiveTake(
      snapshotFromVisual({
        platform: visual?.platform ?? null,
        cells: visual?.cells ?? [],
        openings: visual?.openings ?? [],
        light: visual?.light ?? null,
        objects: state?.objects ?? [],
        camera_path: state?.camera_path ?? { version: 1, duration_ms: 0, samples: [] },
        faces: visual?.faces,
      }),
    )
  }, [engineRef, platformVisualRef, stampActiveTake])

  const restoreHistoryEntry = useCallback(
    (entry: HistoryEntry, undone?: HistorySnapshot, skippedNewestFirst?: readonly HistorySnapshot[]) => {
      const snap = entry.snapshot
      skipHistoryRef.current = true
      try {
        const objects = snap.objects ?? []
        // Keep live wall faces — face media is not undoable (server owns blobs; poll would revert undo).
        const faces = facesFromHistorySnapshot(platformVisualRef.current?.faces)
        const nextVisual: PlatformLayerState = {
          platform: snap.platform,
          cells: snap.cells ?? [],
          openings: snap.openings ?? [],
          light: snap.light ?? defaultSceneLight(),
          faces,
          selectedSectors: [],
          selectedFaceTiles: [],
          activeFaceKey: null,
          surfaceGrid: false,
        }
        // Floor first: applySceneState emits objects, and a snapshot taken then must see the undone terrain.
        platformVisualRef.current = nextVisual
        engineRef.current?.setPlatformState(nextVisual)
        const live = engineRef.current?.getSceneState()
        engineRef.current?.applySceneState({
          objects,
          camera_state: live?.camera_state ?? emptySceneLayout().camera_state,
          camera_path: live?.camera_path ?? snap.camera_path,
          camera_paths: live?.camera_paths ?? [],
          active_camera_path_id: live?.active_camera_path_id ?? null,
        })
        if (skippedNewestFirst) {
          engineRef.current?.restoreCameraPathTakesFromHistory({
            patches: historyJumpCameraPatches(snap, skippedNewestFirst),
            activeId: snap.active_camera_path_id ?? null,
          })
        } else {
          const pathEdit = undone?.prior_camera_path
          const cameraTarget = pathEdit
            ? { id: undone?.active_camera_path_id ?? null, path: pathEdit }
            : { id: snap.active_camera_path_id ?? null, path: snap.camera_path }
          engineRef.current?.restoreCameraPathTakeFromHistory(cameraTarget)
        }
        const restored = engineRef.current?.getSceneState()
        const restoredPath = restored?.camera_path ?? live?.camera_path ?? snap.camera_path
        setObjects(objects)
        setCameraPath(restoredPath)
        const startRestore = startCameraAfterHistoryRestore(startCameraRef.current, snap, undone, skippedNewestFirst)
        if (startRestore.changed) {
          startCameraRef.current = startRestore.start
          setHasStartCamera(startRestore.start != null)
          engineRef.current?.setCameraPlaceAim(
            cameraPlaceAimFromStart(startRestore.start, restoredPath.authoring?.points.length ?? 0),
          )
        }
        setPlatformLayout((prev) =>
          normalizeSceneLayout({
            ...emptySceneLayout(),
            ...prev,
            exists: true,
            objects,
            platform: snap.platform,
            cells: snap.cells,
            openings: snap.openings,
            light: snap.light,
            faces,
            camera_path: restoredPath,
            camera_paths: restored?.camera_paths,
            active_camera_path_id: restored?.active_camera_path_id,
          }),
        )
        dirtyRef.current = true
        existsRef.current = true
        setExists(true)
        setHistoryOpen(false)
        queuePersist()
      } finally {
        skipHistoryRef.current = false
      }
    },
    [
      dirtyRef,
      engineRef,
      existsRef,
      platformVisualRef,
      queuePersist,
      setExists,
      setHasStartCamera,
      setHistoryOpen,
      setObjects,
      setCameraPath,
      setPlatformLayout,
      skipHistoryRef,
      startCameraRef,
    ],
  )

  const rewindHistoryTo = useCallback(
    (entry: HistoryEntry) => {
      const rewound = rewindActionHistory(historyKey, entry.id)
      setHistoryEntries(rewound.entries)
      setRedoStack(rewound.redo)
      const skippedNewestFirst = [...rewound.redo].reverse().map((item) => item.snapshot)
      restoreHistoryEntry(entry, undefined, skippedNewestFirst)
    },
    [historyKey, restoreHistoryEntry],
  )

  const undoLastAction = useCallback(() => {
    if (historyEntries.length < 2) return
    const current = historyEntries[0]
    const previous = historyEntries[1]
    if (!current || !previous) return
    setRedoStack((stack) => [current, ...stack])
    restoreHistoryEntry(previous, current.snapshot)
    setHistoryEntries(popNewestActionHistory(historyKey))
  }, [historyEntries, historyKey, restoreHistoryEntry])

  const redoLastAction = useCallback(() => {
    const entry = redoStack[0]
    if (!entry) return
    restoreHistoryEntry(entry)
    const next = pushActionHistory(historyKey, {
      id: entry.id,
      at: entry.at,
      labelKey: entry.labelKey,
      labelParams: entry.labelParams,
      snapshot: entry.snapshot,
    })
    setHistoryEntries(next)
    setRedoStack((stack) => stack.slice(1))
  }, [historyKey, redoStack, restoreHistoryEntry])

  const resetHistoryToBaseline = useCallback(
    (snapshot: HistorySnapshot) => {
      clearActionHistory(historyKey)
      const seeded = seedBaselineHistory(historyKey, snapshot)
      setHistoryEntries(seeded)
      setRedoStack([])
    },
    [historyKey],
  )

  const clearAndReloadHistory = useCallback(() => {
    clearActionHistory(historyKey)
    setHistoryEntries([])
    setRedoStack([])
  }, [historyKey])

  const loadOrSeedBaseline = useCallback(
    (captureSnapshot: () => HistorySnapshot) => {
      const next = ensureBaselineHistory(historyKey, captureSnapshot)
      setHistoryEntries(next)
      setRedoStack([])
      return next
    },
    [historyKey],
  )

  const clearHistoryRedo = useCallback(() => {
    setRedoStack([])
  }, [])

  return {
    historyKey,
    historyEntries,
    setHistoryEntries,
    redoStack,
    setRedoStack,
    pushHistorySnapshot,
    pushHistorySnapshotRef,
    captureCurrentSnapshot,
    restoreHistoryEntry,
    rewindHistoryTo,
    undoLastAction,
    redoLastAction,
    resetHistoryToBaseline,
    clearAndReloadHistory,
    clearHistoryRedo,
    loadOrSeedBaseline,
  }
}
